/**
 * Utility functions for converting between HTML and DOCX formats
 * Uses the 'docx' library which is browser-compatible (no Node.js polyfills required)
 */

import { Document, Paragraph, TextRun, convertInchesToTwip } from 'docx';
import { Packer } from 'docx';

/**
 * Parse HTML and extract text content with basic formatting
 * Returns array of text chunks with their formatting info
 */
function parseHtmlForDocx(htmlContent: string): Array<{ text: string; bold?: boolean; italics?: boolean }> {
  const chunks: Array<{ text: string; bold?: boolean; italics?: boolean }> = [];
  
  // Create a temporary DOM element to parse HTML
  const temp = document.createElement('div');
  temp.innerHTML = htmlContent;
  
  // Recursively walk the DOM tree and extract text with formatting
  function walkDOM(node: Node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (text.trim()) {
        chunks.push({ text });
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      const tagName = element.tagName.toLowerCase();

      // Check for formatting tags
      const isBold = tagName === 'b' || tagName === 'strong';
      const isItalics = tagName === 'i' || tagName === 'em';

      // Handle line breaks
      if (tagName === 'br') {
        chunks.push({ text: '\n' });
      } else if (tagName === 'p' || tagName === 'div') {
        // Process children
        for (let i = 0; i < element.childNodes.length; i++) {
          walkDOM(element.childNodes[i]);
        }
        chunks.push({ text: '\n' });
      } else {
        // Process children for formatting tags (b, strong, i, em)
        for (let i = 0; i < element.childNodes.length; i++) {
          const childNode = element.childNodes[i];
          if (childNode.nodeType === Node.TEXT_NODE) {
            const text = childNode.textContent || '';
            if (text.trim()) {
              chunks.push({
                text,
                bold: isBold,
                italics: isItalics
              });
            }
          } else {
            walkDOM(childNode);
          }
        }
      }
    }
  }
  
  walkDOM(temp);
  
  return chunks.filter(chunk => chunk.text.length > 0);
}

/**
 * Convert HTML content to DOCX Blob using docx library
 * @param htmlContent - HTML content to convert
 * @param fileName - Optional file name for the document (used for styling)
 * @returns Promise<Blob> - DOCX file as a Blob
 */
export async function htmlToDocxBlob(htmlContent: string, fileName: string = 'document'): Promise<Blob> {
  try {
    // Parse HTML content
    const chunks = parseHtmlForDocx(htmlContent);
    
    // Create paragraphs from parsed chunks
    const paragraphs: Paragraph[] = [];
    let currentParagraphText: Array<TextRun> = [];
    
    for (const chunk of chunks) {
      if (chunk.text === '\n') {
        // Create paragraph with accumulated text
        if (currentParagraphText.length > 0) {
          paragraphs.push(new Paragraph({ children: currentParagraphText }));
          currentParagraphText = [];
        } else {
          // Empty line
          paragraphs.push(new Paragraph({ text: '' }));
        }
      } else {
        // Add text run with formatting
        currentParagraphText.push(
          new TextRun({
            text: chunk.text,
            bold: chunk.bold,
            italics: chunk.italics,
          })
        );
      }
    }
    
    // Add any remaining text as final paragraph
    if (currentParagraphText.length > 0) {
      paragraphs.push(new Paragraph({ children: currentParagraphText }));
    }
    
    // Create document
    const doc = new Document({
      sections: [
        {
          children: paragraphs.length > 0 ? paragraphs : [new Paragraph({ text: htmlContent })],
        },
      ],
    });
    
    // Pack document to blob
    const blob = await Packer.toBlob(doc);
    return blob;
  } catch (error) {
    throw new Error(`Failed to convert HTML to DOCX: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Convert Blob to base64 string for transmission
 * @param blob - Blob to convert
 * @returns Promise<string> - Base64 encoded string
 */
export async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Extract base64 part (remove data:application/octet-stream;base64, prefix)
      const base64 = result.split(',')[1] || result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Convert base64 string to Blob
 * @param base64String - Base64 encoded string
 * @param mimeType - MIME type of the blob
 * @returns Blob
 */
export function base64ToBlob(base64String: string, mimeType: string = 'application/octet-stream'): Blob {
  const byteCharacters = atob(base64String);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
}
